import * as React from "react";
import { Page, Card, Table, Alert, Text, Button, Grid, Form } from "tabler-react";
import TradesSiteWrapper from "./TradesSiteWrapper.react";
import RefreshButton from "./RefreshButton.react";
import {
  getStrategyFleet,
  getTokenStatus,
  enableStrategyLive,
  disableStrategyLive,
  fmtCurrency,
  fmtAvgPnl,
  todayIST,
  pnlClass,
} from "./api";

function weekStartIST() {
  const [year, month, day] = todayIST().split("-").map(Number);
  const current = new Date(Date.UTC(year, month - 1, day));
  current.setUTCDate(current.getUTCDate() - ((current.getUTCDay() + 6) % 7));
  return current.toISOString().slice(0, 10);
}

class FleetPage extends React.Component {
  state = {
    loading: true,
    error: "",
    fleetEnabled: true,
    rows: [],
    csrfToken: "",
    togglingId: "",
    planRequired: false,
    dateFrom: "",
    dateTo: "",
    sortKey: "ending_capital",
    sortDirection: "desc",
    detail: null,
  };

  componentDidMount() {
    this.load();
    document.addEventListener("keydown", this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
  }

  onKey = (e) => {
    if (e.key === "Escape") this.closeDetail();
  };

  openDetail = (row) => this.setState({ detail: row });

  closeDetail = () => this.setState({ detail: null });

  fleetQuery() {
    const query = {};
    if (this.state.dateFrom) query.date_from = this.state.dateFrom;
    if (this.state.dateTo) query.date_to = this.state.dateTo;
    return query;
  }

  load = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const [resp, token] = await Promise.all([
        getStrategyFleet(this.fleetQuery()),
        getTokenStatus(),
      ]);
      this.setState({
        rows: resp.strategies || resp.rows || [],
        fleetEnabled: resp.enabled !== false,
        csrfToken: (token && token.csrf_token) || "",
        planRequired: Boolean(token && token.plan_required),
        loading: false,
      });
    } catch (e) {
      this.setState({ error: e.message || "Failed to load fleet", loading: false });
    }
  };

  toggleLive = async (row) => {
    const csrf = (this.state.csrfToken || "").trim();
    if (!csrf) {
      this.setState({ error: "Cannot change strategy: refresh the page and try again." });
      return;
    }
    const strategyId = row.strategy_id;
    const label = row.display_name || strategyId;
    const currentlyLive = Boolean(row.live_enabled);
    if (!currentlyLive && this.state.planRequired) {
      this.setState({ error: "Purchase a valid plan." });
      return;
    }
    if (currentlyLive) {
      if (!window.confirm(`Disable LIVE for "${label}"? Shadow evaluation continues.`)) return;
    } else if (
      !window.confirm(
        `Enable LIVE for "${label}"? Our app tracks its allocation while broker positions are shared.`
      )
    ) {
      return;
    }
    this.setState({ togglingId: strategyId, error: "" });
    try {
      if (currentlyLive) {
        await disableStrategyLive(strategyId, csrf);
      } else {
        await enableStrategyLive(strategyId, csrf);
      }
      const resp = await getStrategyFleet(this.fleetQuery());
      this.setState({
        rows: resp.strategies || resp.rows || [],
        fleetEnabled: resp.enabled !== false,
        togglingId: "",
      });
    } catch (e) {
      this.setState({
        error: e.message || "Failed to update strategy live flag",
        togglingId: "",
      });
    }
  };

  sortBy = (key) => {
    this.setState((state) => ({
      sortKey: key,
      sortDirection: state.sortKey === key && state.sortDirection === "asc" ? "desc" : "asc",
    }));
  };

  setRange = (dateFrom, dateTo) => {
    this.setState({ dateFrom, dateTo }, this.load);
  };

  sortedRows(rows) {
    const { sortKey, sortDirection } = this.state;
    const direction = sortDirection === "asc" ? 1 : -1;
    const numericKeys = new Set([
      "ending_capital",
      "rating",
      "trades",
      "win_rate",
      "pnl_rupees",
      "live_open",
    ]);
    return [...rows].sort((a, b) => {
      if (numericKeys.has(sortKey)) {
        const left = sortKey === "live_open" ? (a[sortKey] ? 1 : 0) : Number(a[sortKey]);
        const right = sortKey === "live_open" ? (b[sortKey] ? 1 : 0) : Number(b[sortKey]);
        const leftMissing = sortKey !== "live_open" && !Number.isFinite(left);
        const rightMissing = sortKey !== "live_open" && !Number.isFinite(right);
        if (leftMissing || rightMissing) return leftMissing === rightMissing ? 0 : leftMissing ? 1 : -1;
        return (left - right) * direction;
      }
      const left = String(a.display_name || a.strategy_id || "").toLowerCase();
      const right = String(b.display_name || b.strategy_id || "").toLowerCase();
      return left.localeCompare(right) * direction;
    });
  }

  renderSortHeader(label, key) {
    const active = this.state.sortKey === key;
    const direction = active ? this.state.sortDirection : "";
    const indicator = active ? (direction === "asc" ? " ▲" : " ▼") : "";
    return (
      <Table.ColHeader aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}>
        <button
          type="button"
          onClick={() => this.sortBy(key)}
          title={`Sort by ${label}`}
          aria-label={`Sort by ${label}${active ? `, currently ${direction === "asc" ? "ascending" : "descending"}` : ""}`}
          style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", font: "inherit", color: "inherit" }}
        >
          {label}{indicator}
        </button>
      </Table.ColHeader>
    );
  }

  renderLiveCell(row) {
    const { togglingId, planRequired } = this.state;
    const busy = togglingId === row.strategy_id;
    const shadowOnly = row.supports_live === false && !row.live_enabled;
    if (shadowOnly) {
      return <Text muted="true">Shadow only</Text>;
    }
    const needsPlan = planRequired && !row.live_enabled;
    return (
      <span
        title={needsPlan ? "Purchase a valid plan." : undefined}
        style={needsPlan ? { display: "inline-block", cursor: "not-allowed" } : undefined}
      >
        <span style={needsPlan ? { pointerEvents: "none" } : undefined}>
          <Button
            size="sm"
            color={row.live_enabled ? "danger" : "primary"}
            disabled={busy || needsPlan}
            onClick={() => this.toggleLive(row)}
          >
            {busy ? "…" : row.live_enabled ? "Deactivate" : "Activate"}
          </Button>
        </span>
      </span>
    );
  }

  renderRatingCell(row) {
    const labels = {
      insufficient_data: "Insufficient data",
      review_risk: "Review risk",
      review_edge: "Review edge",
      live_candidate: "Live candidate",
    };
    const tone = row.rating_status === "live_candidate" ? "success" : row.rating_status === "insufficient_data" ? "secondary" : "warning";
    const history = (row.rating_history || [])
      .filter((entry) => entry.rank != null)
      .map((entry) => `${entry.date}: #${entry.rank} (${Number(entry.rating || 0).toFixed(1)})`)
      .join("\n");
    const historyTitle = history
      ? `Cumulative shadow rating rank\n30d average: ${row.rating_rank_30d == null ? "—" : `#${row.rating_rank_30d}`}\n5d average: ${row.rating_rank_5d == null ? "—" : `#${row.rating_rank_5d}`}\nTop 5: ${row.rating_top5_days_30d || 0}/${row.rating_observed_days_30d || 0} rated days\n\n${history}`
      : "No rated history yet. A strategy needs 10 closed shadow trades.";
    return (
      <div>
        <span className={`badge badge-${tone}`}>
          {row.rating == null ? "—" : `${Number(row.rating).toFixed(1)}/100`}
        </span>
        <span
          title={historyTitle}
          aria-label="Show rating history"
          role="img"
          className="ml-1 text-muted"
          style={{ cursor: "help" }}
        >
          ⓘ
        </span>
        <div className="small mt-1">{labels[row.rating_status] || "Review"}</div>
        <Text muted="true" className="small">
          {row.trades} trades · Exp {row.expectancy == null ? "—" : Number(row.expectancy).toFixed(2)}
          {" · W/L "}
          {row.payoff_ratio == null ? "—" : Number(row.payoff_ratio).toFixed(2)}
        </Text>
        {row.rating_reasons && row.rating_reasons[0] ? (
          <Text muted="true" className="small">{row.rating_reasons[0]}</Text>
        ) : null}
      </div>
    );
  }

  renderDetail() {
    const r = this.state.detail;
    if (!r) return null;
    const name = r.display_name || r.strategy_id;
    const steps = Array.isArray(r.steps) ? r.steps : [];
    const trades = Number(r.trades || 0);
    const wins = Number(r.wins || 0);
    const losses = Number(r.losses || 0);
    return (
      <div
        className="modal d-block"
        style={{ background: "rgba(15,23,42,.45)" }}
        onClick={this.closeDetail}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fleet-learn-title"
      >
        <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title" id="fleet-learn-title">
                {name}
              </h3>
              <button
                type="button"
                className="close"
                onClick={this.closeDetail}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <h4>How it works</h4>
              <p>{r.description || "No short description is stored for this strategy."}</p>
              {steps.length ? (
                <ol className="pl-3">
                  {steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              ) : (
                <Text muted="true">No step-by-step playbook is stored for this strategy.</Text>
              )}
              <h4 className="mt-4">How we count a win</h4>
              <p>
                Closed paper trades in the date range on this page. A win is a closed
                trade whose points P&amp;L is above zero. Win % is wins divided by
                closed trades. Open trades and skips are not counted. Avg win is the
                mean rupee P&amp;L of winning trades (one lot). Avg loss is the mean
                of losing trades. The ₹50 round-trip cost is only in End capital.
              </p>
              <Grid.Row>
                <Grid.Col md={4}>
                  <Text muted="true" className="small">Win %</Text>
                  <div>
                    <strong>{Number(r.win_rate || 0).toFixed(1)}%</strong>
                    <Text muted="true" className="small">{trades} closed trades</Text>
                  </div>
                </Grid.Col>
                <Grid.Col md={4}>
                  <Text muted="true" className="small">Avg win</Text>
                  <div className={pnlClass(r.avg_win || 0)}>
                    <strong>{fmtAvgPnl(r.avg_win, wins)}</strong>
                    <Text muted="true" className="small">{wins} wins</Text>
                  </div>
                </Grid.Col>
                <Grid.Col md={4}>
                  <Text muted="true" className="small">Avg loss</Text>
                  <div className={pnlClass(r.avg_loss || 0)}>
                    <strong>{fmtAvgPnl(r.avg_loss, losses)}</strong>
                    <Text muted="true" className="small">{losses} losses</Text>
                  </div>
                </Grid.Col>
              </Grid.Row>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, rows, fleetEnabled, dateFrom, dateTo } = this.state;
    return (
      <TradesSiteWrapper>
        <Page.Content
          title="Strategy Fleet"
          subTitle="Shadow-only performance — not actual broker P&L. End capital uses one-lot rupee conversion and ₹50 per completed trade."
          options={<RefreshButton onClick={this.load} loading={loading} />}
        >
          <Alert type="info">
            <span data-page-note="1">
              Enable live only when that strategy should send orders to your trading account. Turning live off keeps shadow evaluation running. Figures here are shadow performance, not your broker P&amp;L.
            </span>
          </Alert>
          {error ? <Alert type="danger">{error}</Alert> : null}
          {!fleetEnabled ? (
            <Alert type="warning">Strategy fleet is disabled on this server.</Alert>
          ) : null}
          {loading ? <Text>Loading fleet…</Text> : null}
          {!loading && !error && fleetEnabled ? (
            <React.Fragment>
              <Card className="mb-3">
                <Card.Body>
                  <Grid.Row>
                    <Grid.Col md={3}>
                      <Form.Group label="From date">
                        <Form.Input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => this.setState({ dateFrom: e.target.value })}
                        />
                      </Form.Group>
                    </Grid.Col>
                    <Grid.Col md={3}>
                      <Form.Group label="To date">
                        <Form.Input
                          type="date"
                          value={dateTo}
                          onChange={(e) => this.setState({ dateTo: e.target.value })}
                        />
                      </Form.Group>
                    </Grid.Col>
                    <Grid.Col md={2}>
                      <Form.Group label={"\u00a0"}>
                        <Button color="primary" block onClick={this.load} disabled={loading}>
                          Apply filter
                        </Button>
                      </Form.Group>
                    </Grid.Col>
                    <Grid.Col md={4}>
                      <Form.Group label={"\u00a0"}>
                        <Button.List>
                          <Button
                            color="secondary"
                            outline
                            onClick={() => {
                              const today = todayIST();
                              this.setRange(today, today);
                            }}
                            disabled={loading}
                          >
                            Today
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            onClick={() => this.setRange(weekStartIST(), todayIST())}
                            disabled={loading}
                          >
                            This week
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            onClick={() => this.setRange("", "")}
                            disabled={loading}
                          >
                            All time
                          </Button>
                        </Button.List>
                      </Form.Group>
                    </Grid.Col>
                  </Grid.Row>
                </Card.Body>
              </Card>
              <Card>
              <Card.Header>
                <Card.Title>Shadow fleet strategies ({rows.length})</Card.Title>
              </Card.Header>
              <Table responsive highlightRowOnHover>
                <Table.Header>
                  <Table.Row>
                    <Table.ColHeader>Strategy name</Table.ColHeader>
                    {this.renderSortHeader("Win %", "win_rate")}
                    {this.renderSortHeader("Shadow P&L (₹)", "pnl_rupees")}
                    {this.renderSortHeader("End capital", "ending_capital")}
                    <Table.ColHeader>Action</Table.ColHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {this.sortedRows(rows).map((r) => {
                    const active = Boolean(r.live_open);
                    return (
                      <Table.Row
                        key={r.strategy_id}
                        style={active ? { backgroundColor: "#d4edda" } : undefined}
                      >
                        <Table.Col>
                          <strong>{r.display_name || r.strategy_id}</strong>
                          {r.description ? (
                            <Text muted="true" className="small d-block">{r.description}</Text>
                          ) : null}
                          <Button
                            size="sm"
                            outline
                            color="secondary"
                            className="mt-2"
                            onClick={() => this.openDetail(r)}
                          >
                            Learn more
                          </Button>
                        </Table.Col>
                        <Table.Col>
                          {Number(r.win_rate || 0).toFixed(1)}%
                          <Text muted="true" className="small">{r.trades} trades</Text>
                        </Table.Col>
                        <Table.Col className={pnlClass(r.pnl_rupees || 0)}>{fmtCurrency(r.pnl_rupees || 0)}</Table.Col>
                        <Table.Col
                          className={pnlClass((r.ending_capital || 100000) - (r.starting_capital || 100000))}
                          title="₹1,00,000 starting capital; one-lot rupee conversion; ₹50 per completed trade"
                        >
                          <strong>{fmtCurrency(r.ending_capital || r.starting_capital || 100000)}</strong>
                          <Text muted="true" className="small">
                            {Number(r.capital_return_pct || 0).toFixed(2)}% after costs
                          </Text>
                        </Table.Col>
                        <Table.Col>{this.renderLiveCell(r)}</Table.Col>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
              </Card>
            </React.Fragment>
          ) : null}
          {this.renderDetail()}
        </Page.Content>
      </TradesSiteWrapper>
    );
  }
}

export default FleetPage;
